import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import React from 'react'
import { merchantTheme } from 'theme'

const MAX_DISPLAY_COUNT = 99
const OVERFLOW_LABEL = '99+'

// Tab badges (Escalations parent tab, Activity sub-tab) stay at 'sm';
// the dashboard Needs Attention row uses 'lg'.
const SIZE_STYLES = {
  sm: { fontSize: '10px', padding: '1px 6px', borderRadius: '8px' },
  // lg matches the DS Alert badge spec: 4px/8px padding, 4px radius.
  lg: { fontSize: '15px', padding: '4px 8px', borderRadius: '4px' },
  // Location filter trigger's selected count (Sketch v83 `.f-cnt`): a
  // 16px-tall, fully rounded pill centring one or two digits.
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '16px',
    height: '16px',
    fontSize: '10px',
    fontWeight: 800,
    paddingTop: '0px',
    paddingBottom: '0px',
    paddingLeft: '4px',
    paddingRight: '4px',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    borderBottomRightRadius: '20px',
    borderBottomLeftRadius: '20px',
  },
}

// 'cayenne' (default) is the original/only appearance every existing call
// site (NeedsAttentionRow, RecoveryAI/Landing escalation tab) renders — kept
// as the default so those are visually unchanged. 'warning'/'neutral' are
// added for the Feedback Inbox filter bar's kind-segment pills (Chassis
// PR 03): amber for Order Feedback, grey for RecoveryAI Escalations.
// 'primary' is added for the Location filter trigger's selected-count pill
// (Chassis PR 03, v69 prototype `.f-cnt`, index.html:1110 — filled belize
// bg, white text). 'recoveryAi' is the active RecoveryAI Escalations pill.
type Tone = 'cayenne' | 'warning' | 'neutral' | 'primary' | 'recoveryAi'

const TONE_COLORS: Record<Tone, { background: string; color: string }> = {
  // DS "Alert" badge pairing (see below) — unchanged from before `tone` existed.
  cayenne: {
    background: merchantTheme.colors.cayenne10,
    color: merchantTheme.colors.cayenne70,
  },
  // Active Order Feedback kind segment (v83 `.src-b.on[data-s="comments"]
  // .src-n` — bg #FAF0D1 is an exact match for eggYolk10). The prototype's
  // #A27E11 text is only 3.3:1 on that bg, and even eggYolk70 reaches just
  // 4.3:1, so grey90 is the accessible substitution (same one
  // RecoveryAI/components/StatusPill.tsx makes).
  warning: {
    background: merchantTheme.colors.eggYolk10,
    color: merchantTheme.colors.grey90,
  },
  // Inactive kind segment (v83 default `.src-n` — bg #D6DADE, white text,
  // ~1.4:1). grey90 on grey20 is 8.5:1; grey70 on grey20 (3.95:1) would miss
  // AA for this 10px text.
  neutral: {
    background: merchantTheme.colors.grey20,
    color: merchantTheme.colors.grey90,
  },
  // Active RecoveryAI Escalations segment (v83 `.src-b.on[data-s="esc"]` —
  // #13C1BF, white text, fails AA). Darkened to the family's spearmint90.
  recoveryAi: {
    background: palette.spearmint90,
    color: palette.white,
  },
  // Location filter trigger's selected-count pill (v69 prototype `.f-cnt`,
  // index.html:1110 — bg var(--belize)/#0073BF, white text).
  primary: {
    background: merchantTheme.colors.belizeHole50,
    color: merchantTheme.colors.white,
  },
}

type Props = {
  count: number
  // Required: the visual span is aria-hidden, so this sr-only label is the
  // ONLY way the count reaches assistive tech. Omitting it would ship a count
  // no screen-reader user can perceive.
  srLabel: string
  className?: string
  size?: keyof typeof SIZE_STYLES
  tone?: Tone
}

const CountBadge = ({
  count,
  srLabel,
  className,
  size = 'sm',
  tone = 'cayenne',
}: Props) => {
  const [css] = useStyletron()

  // Hide-at-zero: a not-applicable/empty count renders nothing.
  if (count <= 0) return null

  const display = count > MAX_DISPLAY_COUNT ? OVERFLOW_LABEL : String(count)

  const badgeClass = css({
    display: 'inline-block',
    backgroundColor: TONE_COLORS[tone].background,
    color: TONE_COLORS[tone].color,
    fontWeight: 700,
    lineHeight: '1',
    ...SIZE_STYLES[size],
  })

  return (
    <>
      {/*
        aria-hidden: at a tab call-site the parent <button> already owns its
        accessible name; a descendant label would replace the button text.
        The sr-only sibling carries the contextual count instead.
      */}
      <span
        aria-hidden="true"
        className={`${badgeClass} ${className ?? ''}`.trim()}
      >
        {display}
      </span>
      {srLabel && <span className="sr-only">{srLabel}</span>}
    </>
  )
}

export default CountBadge
